import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  ListChecks,
  NotebookPen,
  Undo2,
  X,
} from 'lucide-react';
import {
  Button,
  Card,
  DataTable,
  DateRangePicker,
  EmptyState,
  ErrorState,
  Modal,
  SegmentedControl,
  Select,
  SkeletonTable,
  StatusPill,
  Textarea,
  toast,
  type DataTableColumn,
} from '../../components/ui';
import { ApiError } from '../../lib/api';
import { formatDate, formatHours, formatMoney } from '../../lib/format';
import { RowCheckbox } from '../invoices/bulk';
import {
  fetchAgreementTimeAll,
  useAgreementTime,
  useCorrectEntries,
  useCorrectionAccess,
  type AgreementDetail,
  type CorrectionField,
  type CorrectionResult,
  type NotesMode,
  type TimeLedgerEntry,
  type TimeLedgerFilters,
} from './api';
import { downloadCsv } from './components';
import { ManagementFeesCard } from './ManagementFeesCard';

// ---------------------------------------------------------------------------
// Time tab — the agreement's entries, filterable, and (for admins) correctable
// in bulk: move a selection to the right task, or write their notes.
//
// Filters live in the URL so "Time by task → 04 Grow" is a link that lands on
// exactly those entries, Back returns to the Overview, and a filtered ledger is
// shareable. The keys are NOT `from`/`to`: `?from=client` already means "opened
// from a client" on this page (`useReturnOrigin`).
// ---------------------------------------------------------------------------

const PAGE_SIZE = 50;
const PAGE_SIZES = [25, 50, 100, 250];
/** Beyond this many pages a jump-to dropdown stops being usable. */
const MAX_PAGE_OPTIONS = 200;

const PARAM: Record<keyof TimeLedgerFilters, string> = {
  from: 'start',
  to: 'end',
  personId: 'person',
  taskId: 'task',
  billable: 'billable',
  invoiced: 'invoiced',
  page: 'page',
  pageSize: 'per',
};
const FILTER_KEYS = ['from', 'to', 'personId', 'taskId', 'billable', 'invoiced'] as const;

/**
 * The search string for this agreement's Time tab filtered to one task —
 * `'none'` for entries with no task. Keeps whatever else the page carries
 * (`?from=client`), drops the rest of the ledger's filters so the link lands
 * on the figure it was clicked from.
 */
export function timeTabSearch(current: URLSearchParams, taskId: string | null): string {
  const next = new URLSearchParams(current);
  for (const k of Object.values(PARAM)) next.delete(k);
  next.set('tab', 'time');
  next.set(PARAM.taskId, taskId ?? 'none');
  return `?${next.toString()}`;
}

function readFilters(params: URLSearchParams): TimeLedgerFilters {
  const flag = (v: string | null) => (v === 'true' || v === 'false' ? v : '');
  return {
    from: params.get(PARAM.from) ?? undefined,
    to: params.get(PARAM.to) ?? undefined,
    personId: params.get(PARAM.personId) ?? undefined,
    taskId: params.get(PARAM.taskId) ?? undefined,
    billable: flag(params.get(PARAM.billable)),
    invoiced: flag(params.get(PARAM.invoiced)),
    page: Math.max(1, Number(params.get(PARAM.page)) || 1),
    pageSize: PAGE_SIZES.includes(Number(params.get(PARAM.pageSize)))
      ? Number(params.get(PARAM.pageSize))
      : PAGE_SIZE,
  };
}

function monthRange(offset: number): { from: string; to: string } {
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1));
  const end = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + 1, 0));
  return { from: start.toISOString().slice(0, 10), to: end.toISOString().slice(0, 10) };
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** What the selection bar needs to remember about a ticked row, across pages. */
interface Picked {
  hours: number;
  /** The note cannot change (an approved timesheet) — the task still can. */
  notesLocked: boolean;
}

/** The last correction, held so it can be undone. */
interface LastCorrection {
  field: CorrectionField;
  sentence: string;
  result: CorrectionResult;
}

export function AgreementTimeTab({ detail }: { detail: AgreementDetail }) {
  const agreementId = detail.agreement.id;
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => readFilters(params), [params]);
  const [exporting, setExporting] = useState(false);
  const query = useAgreementTime(agreementId, filters);
  const access = useCorrectionAccess();
  const canCorrect = access.data?.canCorrect === true;

  const set = (patch: Partial<TimeLedgerFilters>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(patch) as Array<[keyof TimeLedgerFilters, unknown]>) {
          const isDefault =
            value === undefined ||
            value === '' ||
            (key === 'page' && value === 1) ||
            (key === 'pageSize' && value === PAGE_SIZE);
          if (isDefault) next.delete(PARAM[key]);
          else next.set(PARAM[key], String(value));
        }
        // Any filter change goes back to page 1.
        if (!('page' in patch)) next.delete(PARAM.page);
        return next;
      },
      { replace: true },
    );
  const clearFilters = () => set(Object.fromEntries(FILTER_KEYS.map((k) => [k, undefined])));

  const data = query.data;
  const active = FILTER_KEYS.some((k) => !!filters[k]);

  // ---- selection ------------------------------------------------------------
  // Survives paging, not a filter change: a tick made under "04 Grow" must not
  // silently ride along into a different view.
  const [picked, setPicked] = useState<Map<string, Picked>>(new Map());
  const filterKey = FILTER_KEYS.map((k) => filters[k] ?? '').join('|');
  useEffect(() => setPicked(new Map()), [filterKey]);
  const [selectingAll, setSelectingAll] = useState(false);
  const [dialog, setDialog] = useState<CorrectionField | null>(null);
  const [last, setLast] = useState<LastCorrection | null>(null);
  const correct = useCorrectEntries();

  const pageRows = data?.entries ?? [];
  const selectable = (e: TimeLedgerEntry) => !e.locks?.task;
  const pickOf = (e: TimeLedgerEntry): Picked => ({ hours: e.hours, notesLocked: !!e.locks?.notes });
  const pageSelectable = pageRows.filter(selectable);
  const pageAllPicked = pageSelectable.length > 0 && pageSelectable.every((e) => picked.has(e.id));
  const pickedHours = [...picked.values()].reduce((s, p) => s + p.hours, 0);
  const notesLockedCount = [...picked.values()].filter((p) => p.notesLocked).length;

  const togglePage = (checked: boolean) =>
    setPicked((prev) => {
      const next = new Map(prev);
      for (const e of pageSelectable) {
        if (checked) next.set(e.id, pickOf(e));
        else next.delete(e.id);
      }
      return next;
    });
  const toggleRow = (e: TimeLedgerEntry, checked: boolean) =>
    setPicked((prev) => {
      const next = new Map(prev);
      if (checked) next.set(e.id, pickOf(e));
      else next.delete(e.id);
      return next;
    });

  const selectAllMatching = async () => {
    setSelectingAll(true);
    try {
      const all = await fetchAgreementTimeAll(agreementId, filters);
      setPicked(new Map(all.entries.filter(selectable).map((e) => [e.id, pickOf(e)])));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not select every entry');
    } finally {
      setSelectingAll(false);
    }
  };

  const taskName = (id: string | null) =>
    id ? (detail.tasks.find((t) => t.task.id === id)?.task.name ?? 'that task') : 'No task';

  // The bar under the filters says what happened and holds the Undo; a toast
  // repeating it would be the same sentence twice.
  const announce = (field: CorrectionField, result: CorrectionResult, sentence: string) => {
    setPicked(new Map());
    setDialog(null);
    setLast({ field, result, sentence });
  };

  const moveToTask = (taskId: string | null) =>
    correct.mutate(
      { agreementId, field: 'task', ids: [...picked.keys()], taskId },
      {
        onSuccess: (result) =>
          announce(
            'task',
            result,
            `Moved ${plural(result.changed, 'entry', 'entries')} · ${formatHours(result.changedHours, {
              unit: true,
            })} to ${taskName(taskId)}`,
          ),
        onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Could not move the entries'),
      },
    );

  const writeNotes = (mode: NotesMode, text: string) =>
    correct.mutate(
      { agreementId, field: 'notes', ids: [...picked.keys()], mode, text },
      {
        onSuccess: (result) =>
          announce(
            'notes',
            result,
            `Updated the note on ${plural(result.changed, 'entry', 'entries')} · ${formatHours(
              result.changedHours,
              { unit: true },
            )}`,
          ),
        onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Could not update the notes'),
      },
    );

  const undo = () => {
    if (!last || last.result.undo.length === 0) return;
    correct.mutate(
      { agreementId, field: last.field, undo: last.result.undo },
      {
        onSuccess: (result) => {
          setLast(null);
          const left = result.skipped.length;
          toast.success(
            `Put back ${plural(result.changed, 'entry', 'entries')}${
              left > 0 ? ` · ${left} changed since and left as they are` : ''
            }`,
          );
        },
        onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Could not undo'),
      },
    );
  };

  // ---- CSV --------------------------------------------------------------------
  const exportCsv = async () => {
    setExporting(true);
    try {
      const all = await fetchAgreementTimeAll(agreementId, filters);
      downloadCsv(`${detail.agreement.code ?? detail.agreement.name}-time.csv`, [
        ['Date', 'Person', 'Task', 'Notes', 'Hours', 'Rate', 'Amount', 'Cost', 'Billable', 'Invoiced'],
        ...all.entries.map((e) => [
          e.date,
          e.person.name,
          e.task?.name ?? '',
          e.notes ?? '',
          e.hours.toFixed(2),
          e.rate != null ? e.rate.toFixed(2) : '',
          e.amount.toFixed(2),
          e.cost.toFixed(2),
          e.billable ? 'yes' : 'no',
          e.invoiced ? 'yes' : 'no',
        ]),
        [
          'Total',
          '',
          '',
          '',
          all.totals.hours.toFixed(2),
          '',
          all.totals.amount.toFixed(2),
          all.totals.cost.toFixed(2),
        ],
      ]);
    } finally {
      setExporting(false);
    }
  };

  // ---- table -------------------------------------------------------------------
  const columns: Array<DataTableColumn<TimeLedgerEntry>> = [
    ...(canCorrect
      ? [
          {
            key: 'select',
            header: (
              <RowCheckbox
                checked={pageAllPicked}
                indeterminate={picked.size > 0}
                disabled={pageSelectable.length === 0}
                onToggle={(checked) => togglePage(checked)}
                label={pageAllPicked ? 'Clear this page' : 'Select every entry on this page'}
              />
            ),
            render: (e: TimeLedgerEntry) => (
              <RowCheckbox
                checked={picked.has(e.id)}
                disabled={!selectable(e)}
                onToggle={(checked) => toggleRow(e, checked)}
                label={
                  e.locks?.task ??
                  `Select ${e.person.name}'s ${formatHours(e.hours, { unit: true })} on ${formatDate(e.date)}`
                }
              />
            ),
            className: 'w-8 pl-3 pr-1',
          },
        ]
      : []),
    {
      key: 'date',
      header: 'Date',
      render: (e) => <span className="num whitespace-nowrap">{formatDate(e.date)}</span>,
    },
    { key: 'person', header: 'Person', render: (e) => e.person.name },
    {
      key: 'task',
      header: 'Task',
      render: (e) =>
        e.task ? <span className="text-grey-600">{e.task.name}</span> : <span className="text-grey-400">—</span>,
    },
    {
      key: 'notes',
      header: 'Notes',
      className: 'max-w-[14rem]',
      render: (e) => (
        <span className="block truncate text-grey-600" title={e.notes ?? undefined}>
          {e.notes ?? '—'}
        </span>
      ),
    },
    {
      key: 'hours',
      header: 'Hours',
      align: 'right',
      render: (e) => formatHours(e.hours, { fixed: true }),
    },
    {
      key: 'rate',
      header: 'Rate /h',
      align: 'right',
      render: (e) =>
        e.rate != null ? formatMoney(e.rate, { whole: true }) : <span className="text-grey-400">—</span>,
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (e) => (e.billable ? formatMoney(e.amount) : <span className="text-grey-400">—</span>),
    },
    {
      key: 'cost',
      header: 'Cost',
      align: 'right',
      render: (e) => formatMoney(e.cost),
    },
    {
      key: 'status',
      header: 'Status',
      render: (e) => (
        <span className="inline-flex items-center gap-1.5">
          {e.deletedInSource ? (
            <StatusPill status="warning" label="Deleted in Harvest" dot={false} />
          ) : e.invoiced ? (
            <StatusPill status="paid" label="Invoiced" />
          ) : e.billable ? (
            <StatusPill status="open" label="Billable" />
          ) : (
            <StatusPill status="neutral" label="Non-billable" dot={false} />
          )}
          {/* Approved: the review lock's own glyph (see the time screens). Its
              task can still be corrected; its note cannot. */}
          {!e.locks?.task && e.locks?.notes && (
            <span title={e.locks.notes} aria-label={e.locks.notes} className="inline-flex">
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-grey-400" aria-hidden />
            </span>
          )}
        </span>
      ),
    },
  ];

  const totals = data?.totals;
  const page = data?.page ?? 1;
  const totalPages = data?.totalPages ?? 1;
  const pageSize = filters.pageSize ?? PAGE_SIZE;

  return (
    <div className="space-y-4">
      <Card padding="sm">
        <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
          <DateRangePicker
            label="Date range"
            className="w-full"
            value={{ from: filters.from ?? '', to: filters.to ?? '' }}
            onChange={(range) => set({ from: range.from, to: range.to })}
            presets={[
              { label: 'This month', range: () => monthRange(0) },
              { label: 'Last month', range: () => monthRange(-1) },
            ]}
          />
          <Select
            label="Person"
            selectSize="sm"
            className="w-[calc(50%-0.25rem)] sm:w-44"
            options={[
              { value: '', label: 'Everyone' },
              ...(data?.facets.people ?? []).map((p) => ({ value: p.id, label: p.name })),
            ]}
            value={filters.personId ?? ''}
            onChange={(e) => set({ personId: e.target.value })}
          />
          <Select
            label="Task"
            selectSize="sm"
            className="w-[calc(50%-0.25rem)] sm:w-44"
            options={[
              { value: '', label: 'All tasks' },
              ...(data?.facets.tasks ?? []).map((t) => ({ value: t.id, label: t.name })),
              { value: 'none', label: 'No task' },
            ]}
            value={filters.taskId ?? ''}
            onChange={(e) => set({ taskId: e.target.value })}
          />
          <Select
            label="Billable"
            selectSize="sm"
            className="w-[calc(50%-0.25rem)] sm:w-36"
            options={[
              { value: '', label: 'Any' },
              { value: 'true', label: 'Billable' },
              { value: 'false', label: 'Non-billable' },
            ]}
            value={filters.billable ?? ''}
            onChange={(e) => set({ billable: e.target.value as TimeLedgerFilters['billable'] })}
          />
          <Select
            label="Invoiced"
            selectSize="sm"
            className="w-[calc(50%-0.25rem)] sm:w-36"
            options={[
              { value: '', label: 'Any' },
              { value: 'false', label: 'Unbilled' },
              { value: 'true', label: 'Invoiced' },
            ]}
            value={filters.invoiced ?? ''}
            onChange={(e) => set({ invoiced: e.target.value as TimeLedgerFilters['invoiced'] })}
          />
          <div className="flex flex-wrap items-end gap-2 sm:ml-auto">
            {active && (
              <Button size="sm" variant="ghost" onClick={clearFilters}>
                Clear
              </Button>
            )}
            <Button size="sm" variant="outline" loading={exporting} onClick={exportCsv}>
              <Download className="h-4 w-4" aria-hidden />
              Export CSV
            </Button>
          </div>
        </div>
      </Card>

      {canCorrect && picked.size > 0 && (
        <SelectionBar
          count={picked.size}
          hours={pickedHours}
          // Only when the filter runs past this page — otherwise the page IS
          // everything selectable, and the offer would promise rows that are locked.
          canSelectAll={pageAllPicked && !!data && data.total > pageRows.length}
          total={data?.total ?? 0}
          selectingAll={selectingAll}
          onSelectAll={() => void selectAllMatching()}
          onMove={() => setDialog('task')}
          onNotes={() => setDialog('notes')}
          onClear={() => setPicked(new Map())}
        />
      )}

      {last && (
        <UndoBar
          last={last}
          undoing={correct.isPending}
          onUndo={undo}
          onDismiss={() => setLast(null)}
        />
      )}

      {query.isError ? (
        <ErrorState
          card
          message={query.error instanceof Error ? query.error.message : undefined}
          onRetry={() => query.refetch()}
        />
      ) : query.isLoading ? (
        <Card>
          <SkeletonTable rows={8} />
        </Card>
      ) : (
        <Card>
          <DataTable
            columns={columns}
            data={pageRows}
            rowKey={(e) => e.id}
            empty={
              <EmptyState
                icon={<Clock />}
                title={active ? 'No entries match these filters' : 'No time tracked yet'}
                description={
                  active
                    ? 'Widen the date range or clear a filter to see more entries.'
                    : 'Entries logged on this agreement in the timesheet will show up here.'
                }
                action={
                  active ? (
                    <Button variant="outline" onClick={clearFilters}>
                      Clear filters
                    </Button>
                  ) : undefined
                }
              />
            }
            footer={
              totals && data && data.total > 0 ? (
                <tr>
                  <td
                    className="px-4 py-2.5 text-caption uppercase tracking-wider text-grey-500"
                    colSpan={canCorrect ? 5 : 4}
                  >
                    Total · {data.total} {data.total === 1 ? 'entry' : 'entries'}
                    {active ? ' (filtered)' : ''}
                  </td>
                  <td className="num-cell px-4 py-2.5">{formatHours(totals.hours, { fixed: true })}</td>
                  <td />
                  <td className="num-cell px-4 py-2.5">{formatMoney(totals.amount)}</td>
                  <td className="num-cell px-4 py-2.5">{formatMoney(totals.cost)}</td>
                  <td />
                </tr>
              ) : undefined
            }
          />
          {data && data.total > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-grey-100 px-4 py-2.5">
              <p className="text-caption text-grey-500">
                {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, data.total)} of {data.total} ·{' '}
                {formatHours(totals?.billableHours ?? 0)} billable h · cost of time only, agreement
                expenses are on the Expenses tab
              </p>
              <div className="flex shrink-0 items-center gap-2">
                <Select
                  aria-label="Entries per page"
                  selectSize="sm"
                  className="w-[6.5rem]"
                  options={PAGE_SIZES.map((n) => ({ value: String(n), label: `${n} / page` }))}
                  value={String(pageSize)}
                  onChange={(e) => set({ pageSize: Number(e.target.value), page: 1 })}
                />
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={page <= 1}
                  aria-label="Previous page"
                  onClick={() => set({ page: page - 1 })}
                >
                  <ChevronLeft className="h-4 w-4" aria-hidden />
                  Prev
                </Button>
                {totalPages > 1 && totalPages <= MAX_PAGE_OPTIONS ? (
                  <Select
                    aria-label="Jump to page"
                    selectSize="sm"
                    className="w-[7.5rem]"
                    options={Array.from({ length: totalPages }, (_, i) => ({
                      value: String(i + 1),
                      label: `Page ${i + 1} of ${totalPages}`,
                    }))}
                    value={String(page)}
                    onChange={(e) => set({ page: Number(e.target.value) })}
                  />
                ) : (
                  <span className="num text-caption text-grey-500">
                    Page {page} of {totalPages}
                  </span>
                )}
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={page >= totalPages}
                  aria-label="Next page"
                  onClick={() => set({ page: page + 1 })}
                >
                  Next
                  <ChevronRight className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      <ManagementFeesCard
        agreementId={agreementId}
        clientId={detail.agreement.client.id}
        from={filters.from}
        to={filters.to}
      />

      {dialog === 'task' && (
        <MoveToTaskDialog
          count={picked.size}
          hours={pickedHours}
          tasks={detail.tasks.map((t) => ({ id: t.task.id, name: t.task.name, active: t.task.isActive }))}
          pending={correct.isPending}
          onClose={() => setDialog(null)}
          onConfirm={moveToTask}
        />
      )}
      {dialog === 'notes' && (
        <NotesDialog
          count={picked.size}
          hours={pickedHours}
          lockedCount={notesLockedCount}
          pending={correct.isPending}
          onClose={() => setDialog(null)}
          onConfirm={writeNotes}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The bars
// ---------------------------------------------------------------------------

function SelectionBar({
  count,
  hours,
  canSelectAll,
  total,
  selectingAll,
  onSelectAll,
  onMove,
  onNotes,
  onClear,
}: {
  count: number;
  hours: number;
  canSelectAll: boolean;
  total: number;
  selectingAll: boolean;
  onSelectAll: () => void;
  onMove: () => void;
  onNotes: () => void;
  onClear: () => void;
}) {
  return (
    <div className="sticky top-16 z-20 rounded-xl bg-secondary px-3 py-2.5 text-white shadow-elevated">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <p className="num flex flex-wrap items-center gap-x-2 text-body-sm font-bold">
          <span>
            {count} selected
            <span className="ml-2 font-medium text-white/80">{formatHours(hours, { unit: true })}</span>
          </span>
          {canSelectAll && (
            <button
              type="button"
              onClick={onSelectAll}
              disabled={selectingAll}
              className="rounded-lg px-1.5 py-1 text-caption font-semibold text-white/80 underline-offset-2 hover:bg-white/10 hover:text-white hover:underline"
            >
              {selectingAll ? 'Selecting…' : `Select all ${total} matching this filter`}
            </button>
          )}
          <button
            type="button"
            onClick={onClear}
            title="Clear the selection"
            className="inline-flex items-center gap-1 rounded-lg px-1.5 py-1 text-caption font-semibold text-white/70 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X className="h-3.5 w-3.5" aria-hidden />
            Clear
          </button>
        </p>
        <div className="flex flex-1 flex-wrap items-center justify-end gap-1.5">
          <Button size="sm" variant="accent" onClick={onMove}>
            <ListChecks className="h-4 w-4" aria-hidden />
            Move to task…
          </Button>
          <Button size="sm" variant="outline" onClick={onNotes}>
            <NotebookPen className="h-4 w-4" aria-hidden />
            Edit notes…
          </Button>
        </div>
      </div>
    </div>
  );
}

function UndoBar({
  last,
  undoing,
  onUndo,
  onDismiss,
}: {
  last: LastCorrection;
  undoing: boolean;
  onUndo: () => void;
  onDismiss: () => void;
}) {
  const { result } = last;
  const skipped = result.skipped;
  return (
    <div className="rounded-xl border border-grey-200 bg-white px-3 py-2.5 shadow-card" role="status">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <p className="min-w-0 flex-1 text-body-sm text-secondary">
          {result.changed > 0 ? (
            <span className="font-semibold">{last.sentence}.</span>
          ) : result.skipped.length > 0 ? (
            <span className="font-semibold">Nothing was changed — every selected entry is locked.</span>
          ) : (
            <span className="font-semibold">Nothing to change.</span>
          )}
          {result.unchanged > 0 && (
            <span className="text-grey-500"> {plural(result.unchanged, 'entry', 'entries')} already read that way.</span>
          )}
        </p>
        {result.undo.length > 0 && (
          <Button size="sm" variant="outline" onClick={onUndo} loading={undoing}>
            <Undo2 className="h-4 w-4" aria-hidden />
            Undo
          </Button>
        )}
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="rounded-lg p-1 text-grey-400 hover:bg-grey-100 hover:text-grey-600"
        >
          <X className="h-4 w-4" aria-hidden />
        </button>
      </div>
      {skipped.length > 0 && (
        <details className="mt-2 text-caption text-grey-600">
          <summary className="cursor-pointer font-semibold text-amber-700">
            {plural(skipped.length, 'entry was', 'entries were')} left alone ·{' '}
            {formatHours(
              skipped.reduce((s, e) => s + e.hours, 0),
              { unit: true },
            )}
          </summary>
          <ul className="mt-1.5 max-h-48 space-y-1 overflow-y-auto">
            {skipped.map((s) => (
              <li key={s.id}>
                <span className="num font-semibold text-secondary">{formatDate(s.date)}</span> · {s.person} ·{' '}
                <span className="num">{formatHours(s.hours, { unit: true })}</span> — {s.reason}
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The two actions
// ---------------------------------------------------------------------------

const NONE = '__none__';

function MoveToTaskDialog({
  count,
  hours,
  tasks,
  pending,
  onClose,
  onConfirm,
}: {
  count: number;
  hours: number;
  tasks: Array<{ id: string; name: string; active: boolean }>;
  pending: boolean;
  onClose: () => void;
  onConfirm: (taskId: string | null) => void;
}) {
  const [choice, setChoice] = useState('');
  // In the firm's process order, as the server sends them.
  const sorted = tasks;
  return (
    <Modal
      open
      onOpenChange={(open) => !open && !pending && onClose()}
      title={`Move ${plural(count, 'entry', 'entries')} to a task`}
      description={`${formatHours(hours, { unit: true })} selected. Hours, rates and billing stay exactly as they are — only the task changes, including in approved timesheets.`}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            onClick={() => onConfirm(choice === NONE ? null : choice)}
            disabled={!choice}
            loading={pending}
          >
            Move {count}
          </Button>
        </>
      }
    >
      <Select
        label="Task"
        value={choice}
        onChange={(e) => setChoice(e.target.value)}
        options={[
          { value: '', label: 'Choose a task…' },
          ...sorted.map((t) => ({ value: t.id, label: t.active ? t.name : `${t.name} (archived)` })),
          { value: NONE, label: 'No task' },
        ]}
      />
      <p className="mt-3 text-caption text-grey-500">
        Entries on an invoice, deleted in Harvest, or synced from Harvest are left alone and listed after.
      </p>
    </Modal>
  );
}

const MODE_COPY: Record<NotesMode, { label: string; hint: string }> = {
  append: {
    label: 'Add to the note',
    hint: 'Written after what each entry already says, separated by “ — ”. Nothing is lost.',
  },
  fill: {
    label: 'Only where blank',
    hint: 'Written only on entries with no note. Entries that have one keep it.',
  },
  replace: {
    label: 'Replace the note',
    hint: 'Every selected note becomes exactly this. What they said before is gone (Undo puts it back).',
  },
};

function NotesDialog({
  count,
  hours,
  lockedCount,
  pending,
  onClose,
  onConfirm,
}: {
  count: number;
  hours: number;
  /** Selected entries whose note cannot change (approved timesheets). */
  lockedCount: number;
  pending: boolean;
  onClose: () => void;
  onConfirm: (mode: NotesMode, text: string) => void;
}) {
  const [mode, setMode] = useState<NotesMode>('append');
  const [text, setText] = useState('');
  const trimmed = text.trim();
  return (
    <Modal
      open
      onOpenChange={(open) => !open && !pending && onClose()}
      title={`Edit the notes on ${plural(count, 'entry', 'entries')}`}
      description={`${formatHours(hours, { unit: true })} selected. A note can end up on an invoice line — write it the way a client should read it.`}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(mode, trimmed)} disabled={!trimmed} loading={pending}>
            Update {count}
          </Button>
        </>
      }
    >
      <SegmentedControl<NotesMode>
        ariaLabel="How to write the note"
        value={mode}
        onChange={setMode}
        options={(['append', 'fill', 'replace'] as const).map((m) => ({ value: m, label: MODE_COPY[m].label }))}
        size="sm"
        scrollable
      />
      <p className="mt-2 text-caption text-grey-500">{MODE_COPY[mode].hint}</p>
      <Textarea
        className="mt-3"
        label="Note"
        rows={3}
        value={text}
        onChange={(e) => setText(e.target.value)}
        maxLength={2000}
        autoFocus
      />
      {lockedCount > 0 && (
        <p className="mt-3 text-caption text-amber-700">
          {plural(lockedCount, 'selected entry is', 'selected entries are')} in an approved timesheet —
          {lockedCount === 1 ? ' its note stays' : ' their notes stay'} as it is. Moving them to a task still works.
        </p>
      )}
    </Modal>
  );
}
